// AnniversaryHero — detail-page hero card (W3.1 §5.2).
//
// Renders icon chip + title + meta (date / 距離) + category + description +
// author. `is_anchor=true` adds a gold-tint 「交往紀念日」 pill.
//
// W53.2 D-6 — this renders through `<HeroCard>` rather than a hand-written
// `glass-1 rounded-xl shadow-card` article.
//
// The hand-written card was not a *wrong* card so much as an **unregistered**
// one: it looked like the app's hero, sat in the slot `HERO_SLOTS` had no row
// for, carried no mascot and had nothing asserting any of it. The audit that
// found it also found the registry structurally blind here — `heroFor()` throws
// for an unlisted route, so a page that grew a hero by hand was not an error the
// system could see. Going through the primitive makes the page a **consumer** of
// the table, so the table and the page stop being able to disagree.
//
// `HERO_SLOTS` gaining this row is therefore the actual fix; this component
// changing is the consequence.

import { HeroCard } from "@/components/primitives/HeroCard";
import { HERO_ANNIVERSARY_DETAIL } from "@/lib/screenType";
import { categoryIcon, categoryLabelZh } from "@/lib/anniversary/categoryIcon";
import { countdownLabel } from "@/lib/anniversary/countdown";
import { formatAbsoluteZh, parseDateOnly } from "@/lib/utils/date";
import type { AnniversaryRead } from "@/lib/api/anniversary";

export interface AnniversaryHeroProps {
  anniversary: AnniversaryRead;
}

export function AnniversaryHero({ anniversary }: AnniversaryHeroProps) {
  const Icon = categoryIcon(anniversary.category);
  const days = anniversary.days_until_next ?? null;

  return (
    <HeroCard
      as="article"
      slot={HERO_ANNIVERSARY_DETAIL}
      label={`${anniversary.title} 的紀念日`}
      dataAnchor={anniversary.is_anchor_anniversary}
      testId="anniversary-hero-card"
      // `data-component` is **not** overridden, and that is worth saying.
      // `HeroCard` hard-codes `data-component="HeroCard"` and — unlike `Card` —
      // does not spread `{...rest}`, so a `data-component` prop here is dropped
      // **silently**: TypeScript allows `data-*` on any JSX element, so `tsc`
      // stays green while the attribute never reaches the DOM. The first version
      // of this refactor passed one and appeared to work. The hook that *does*
      // work is `testId` above → `data-testid="anniversary-hero-card"`.
      //
      // The hero's own geometry: responsive padding and the flex column the
      // header / description / author stack needs. The radius is **not**
      // overridden — `HeroCard` is `rounded-lg` (`ADR-0180`) and a second
      // spelling of the same value is how 12 / 16 / 18 coexisted in the first
      // place.
      className="p-5 md:p-6 flex flex-col gap-4"
    >
      <header className="flex items-start gap-3">
        <span
          aria-hidden
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-you-tint text-you-strong"
        >
          <Icon size={22} strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-s-2">
          {anniversary.is_anchor_anniversary ? (
            <span
              className="inline-flex items-center gap-1 self-start rounded-pill bg-gold-tint px-2 py-0.5 text-meta font-semibold text-gold-ink"
              data-testid="anchor-pill"
            >
              交往紀念日
            </span>
          ) : null}
          <h1
            className="font-display text-display-detail text-ink-1 leading-tight"
            data-testid="anniversary-title"
          >
            {anniversary.title}
          </h1>
          <p className="text-body-sm text-ink-2">
            <span className="tabular-nums">{anniversary.date}</span>
            {anniversary.date ? (
              <>
                <span aria-hidden> · </span>
                <span>{formatAbsoluteZh(parseDateOnly(anniversary.date))}</span>
              </>
            ) : null}
            {days !== null ? (
              <>
                <span aria-hidden> · </span>
                <span data-testid="anniversary-countdown-meta">{countdownLabel(days)}</span>
              </>
            ) : null}
          </p>
          <p className="text-body-sm text-ink-3">
            分類:{categoryLabelZh(anniversary.category)}
            {anniversary.custom_category_label ? ` · ${anniversary.custom_category_label}` : null}
          </p>
        </div>
      </header>

      {anniversary.description ? (
        <div
          className="bg-paper-tint2 rounded-lg p-4 text-body-sm text-ink-1 whitespace-pre-line"
          data-testid="anniversary-description"
        >
          {anniversary.description}
        </div>
      ) : null}

      {anniversary.author ? (
        <footer className="flex items-center gap-2 text-meta text-ink-3 border-t border-hairline pt-3">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-paper-deep text-ink-2 text-meta">
            {anniversary.author.display_name.slice(0, 1)}
          </span>
          <span>
            {anniversary.author.display_name}
            {anniversary.is_auto_generated ? " · 系統生成" : " 建立"}
          </span>
        </footer>
      ) : null}
    </HeroCard>
  );
}
